/* eslint import/no-unresolved: 0 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from '@edx/frontend-platform/i18n';
import { sendTrackEvent, sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import { Button, Icon, IconButton } from '@openedx/paragon';
import { Close } from '@openedx/paragon/icons';

import { UpgradeButton } from '@src/generic/upgrade-button';
import { useModel } from '@src/generic/model-store';

import { PAGE_NAMES } from '../data';
import CertificatePreview from './certificate-preview';
import {
  ExpirationCountdown,
  AccessExpirationDateBanner,
  PastExpirationDateBanner,
  PastExpirationCardContent,
  UpsellFBESoonCardContent,
  UpsellTable,
} from '../generic';
import getUpsellPageName from './utils';
import messages from './messages';
import { useCohesion } from '../cohesion';

const UpgradeNotification = ({
  id,
  courseId,
  model,
  setNotificationCurrentState,
  toggleSidebar,
}) => {
  const pluginLocationId = id;
  const { formatMessage } = useIntl();

  const {
    org,
    userTimezone,
    verifiedMode,
  } = useModel('courseHomeMeta', courseId);

  const {
    marketingUrl,
    learningAssistantEnabled,
  } = useModel('coursewareMeta', courseId);

  const {
    accessExpiration,
    datesBannerInfo,
    offer,
    timeOffsetMillis,
  } = useModel(model, courseId);

  const { conversion } = useCohesion(courseId);

  const dateNow = Date.now();
  const timezoneFormatArgs = userTimezone ? { timeZone: userTimezone } : {};
  const correctedTime = new Date(dateNow + timeOffsetMillis);
  const accessExpirationDate = accessExpiration ? new Date(accessExpiration.upgradeDeadline) : null;
  const pastExpirationDeadline = accessExpiration ? new Date(dateNow) > accessExpirationDate : false;
  const shouldDisplayBorder = pluginLocationId.includes('outline');
  const upsellPageName = getUpsellPageName({ id: pluginLocationId });
  const setupgradeNotificationCurrentState = setNotificationCurrentState;
  const isFBE = !!accessExpiration && !!datesBannerInfo?.contentTypeGatingEnabled;

  const eventProperties = {
    org_key: org,
    courserun_key: courseId,
  };

  const promotionEventProperties = {
    creative: 'sidebarupsell',
    name: 'In-Course Verification Prompt',
    position: 'sidebar-message',
    promotion_id: 'courseware_verified_certificate_upsell',
    ...eventProperties,
  };

  useEffect(() => {
    sendTrackingLogEvent('edx.bi.course.upgrade.sidebarupsell.displayed', eventProperties);
    sendTrackEvent('Promotion Viewed', promotionEventProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!verifiedMode) {
    return null;
  }

  const logUpgradeClick = () => {
    sendTrackingLogEvent('edx.bi.course.upgrade.sidebarupsell.clicked', eventProperties);
    sendTrackingLogEvent('edx.course.enrollment.upgrade.clicked', {
      ...eventProperties,
      location: 'sidebar-message',
    });
    sendTrackEvent('Promotion Clicked', promotionEventProperties);
    sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
      ...eventProperties,
      linkCategory: 'green_upgrade',
      linkName: `${upsellPageName}_green`,
      linkType: 'button',
      pageName: upsellPageName,
    });

    conversion('Upgraded');
  };

  const logClickPastExpiration = () => {
    sendTrackEvent('edx.bi.ecommerce.upgrade_notification.past_expiration.button_clicked', {
      ...eventProperties,
      linkCategory: 'upgrade_notification',
      linkName: `${upsellPageName}_course_details`,
      linkType: 'button',
      pageName: upsellPageName,
    });
  };

  /*
  There are 5 parts that change in the upgrade card:
    upgradeNotificationHeaderText
    expirationBanner
    upsellMessage
    callToActionButton
    offerCode
  */
  let upgradeNotificationHeaderText;
  let expirationBanner;
  let callToActionButton;
  let offerCode;

  let upsellMessage = <UpsellTable {...{ isFBE, learningAssistantEnabled }} />;

  if (isFBE) {
    const hoursToAccessExpiration = Math.floor((accessExpirationDate - correctedTime) / 1000 / 60 / 60);

    if (hoursToAccessExpiration >= (7 * 24)) {
      if (offer) { // countdown to the first purchase discount if there is one
        const hoursToDiscountExpiration = Math.floor((new Date(offer.expirationDate) - correctedTime) / 1000 / 60 / 60);
        upgradeNotificationHeaderText = formatMessage(
          messages.firstTimeLearnerDiscountHeader,
          { percentage: (offer.percentage) },
        );
        expirationBanner = (
          <ExpirationCountdown
            courseId={courseId}
            hoursToExpiration={hoursToDiscountExpiration}
            setupgradeNotificationCurrentState={setupgradeNotificationCurrentState}
            type="offer"
          />
        );
      } else {
        upgradeNotificationHeaderText = formatMessage(messages.accessExpirationHeader);
        expirationBanner = (
          <AccessExpirationDateBanner
            courseId={courseId}
            accessExpirationDate={accessExpirationDate}
            timezoneFormatArgs={timezoneFormatArgs}
            setupgradeNotificationCurrentState={setupgradeNotificationCurrentState}
          />
        );
      }
    } else if (hoursToAccessExpiration < (7 * 24) && hoursToAccessExpiration >= 0) {
      // more urgent messaging if there's less than 7 days left to access expiration
      upgradeNotificationHeaderText = formatMessage(messages.accessExpirationUrgentHeader);
      expirationBanner = (
        <ExpirationCountdown
          courseId={courseId}
          hoursToExpiration={hoursToAccessExpiration}
          setupgradeNotificationCurrentState={setupgradeNotificationCurrentState}
          type="access"
        />
      );
      if (upsellPageName !== PAGE_NAMES.PROGRESS) {
        upsellMessage = (
          <UpsellFBESoonCardContent
            accessExpirationDate={accessExpirationDate}
            timezoneFormatArgs={timezoneFormatArgs}
          />
        );
      }
    } else { // access expiration deadline has passed
      upgradeNotificationHeaderText = formatMessage(messages.accessExpirationPastHeader);
      expirationBanner = (
        <PastExpirationDateBanner
          courseId={courseId}
          accessExpirationDate={accessExpirationDate}
          timezoneFormatArgs={timezoneFormatArgs}
          setupgradeNotificationCurrentState={setupgradeNotificationCurrentState}
        />
      );
      upsellMessage = (
        <PastExpirationCardContent />
      );
    }
  }

  if (pastExpirationDeadline) {
    callToActionButton = (
      <Button
        variant="primary"
        onClick={logClickPastExpiration}
        href={marketingUrl}
        block
      >
        {formatMessage(messages.pastExpirationCallToActionLabel)}
      </Button>
    );
  } else {
    callToActionButton = (
      <UpgradeButton
        offer={offer}
        onClick={logUpgradeClick}
        verifiedMode={verifiedMode}
        block
      />
    );
  }

  if (offer) { // if there's a first purchase discount, message the code at the bottom
    offerCode = (
      <div className="text-center discount-info">
        {formatMessage(
          messages.discountCodeMessage,
          {
            code: (<span className="font-weight-bold">{offer.code}</span>),
          },
        )}
      </div>
    );
  }

  return (
    <section className={classNames('upgrade-notification small', { 'pgn__card pgn__card-light card mb-4 rounded': shouldDisplayBorder })}>
      <div id="courseHome-upgradeNotification">
        <h2
          className={classNames('upgrade-notification-header', {
            'd-flex align-items-center mr-2 ml-4 my-1.5 font-size-18': !!toggleSidebar,
            'm-4': !toggleSidebar,
          })}
          id="outline-sidebar-upgrade-header"
        >
          {formatMessage(messages.pursueCertificateMessage)}
          {!!toggleSidebar && (
            <div className="d-inline-flex ml-auto">
              <IconButton
                src={Close}
                size="sm"
                iconAs={Icon}
                onClick={toggleSidebar}
                className="icon-hover"
                alt={formatMessage(messages.closeAlt)}
              />
            </div>
          )}
        </h2>
        <CertificatePreview courseId={courseId} />
        <h3 className="ml-4">
          {upgradeNotificationHeaderText}
        </h3>
        {expirationBanner}
        <div className="upgrade-notification-message">
          {upsellMessage}
        </div>
        <div className="upgrade-notification-button m-4">
          {callToActionButton}
        </div>
        {offerCode}
      </div>
    </section>
  );
};

UpgradeNotification.propTypes = {
  id: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  model: PropTypes.string,
  setNotificationCurrentState: PropTypes.func,
  toggleSidebar: PropTypes.func,
};

UpgradeNotification.defaultProps = {
  setNotificationCurrentState: null,
  toggleSidebar: null,
  model: 'outline',
};

export default UpgradeNotification;
